import React, { Component } from "react";
import { getLoyaltyAccounts } from "../../Endpoint/Admin/index";
import { toast } from "react-toastify";
import { ConsolePage } from "../../components/Console/ConsoleLayout";
import {
  C, Panel, StatCard, StatRow, SearchPill, Table, Chip, Btn, Avatar, IconBtn,
} from "../../components/Console/ConsoleUI";

/**
 * Loyalty members — the administration lane's list screen, restyled onto the
 * console shell.
 *
 * Same single getLoyaltyAccounts({page:1, limit:100}) call as before: the API
 * hands back one page plus a total, so the tiles that sum points say plainly
 * that they sum the members on this page, not the whole programme.
 */

const num = (n) => Number(n || 0).toLocaleString();

/** Tier reads as a standing, so it gets a tone rather than a flat badge. */
const tierTone = (tier) => {
  const t = String(tier || "").toLowerCase();
  if (t === "gold" || t === "platinum") return "warn";
  if (t === "silver") return "neutral";
  return "neutral";
};

class LoyaltyAccounts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoaded: false,
      total: 0,
      items: [],
      search: "",
    };
  }

  componentDidMount() {
    this.loadAccounts();
  }

  loadAccounts = () => {
    getLoyaltyAccounts({ page: 1, limit: 100 })
      .then((result) => {
        if (result && result.status === 200 && result.data) {
          const items = result.data.items || [];
          this.setState({
            isLoaded: true,
            total: result.data.total || items.length,
            items,
          });
        } else {
          this.setState({ isLoaded: true });
          toast.error((result && result.message) || "Failed to load loyalty members");
        }
      })
      .catch(() => {
        this.setState({ isLoaded: true });
        toast.error("Error loading loyalty members");
      });
  };

  /** The member's own id — the account may embed the user or just reference it. */
  userIdOf = (a) => (typeof a.userId === "object" && a.userId ? a.userId._id : a.userId);

  nameOf = (a) => {
    const u = (typeof a.userId === "object" && a.userId) || {};
    return `${u.firstName || ""} ${u.lastName || ""}`.trim() || "Unknown";
  };

  contactOf = (a) => {
    const u = (typeof a.userId === "object" && a.userId) || {};
    return u.email || u.mobile || "—";
  };

  visibleRows() {
    const q = this.state.search.trim().toLowerCase();
    if (!q) return this.state.items;
    return this.state.items.filter(
      (a) =>
        this.nameOf(a).toLowerCase().includes(q) ||
        String(this.contactOf(a)).toLowerCase().includes(q)
    );
  }

  openMember = (a) => {
    const id = this.userIdOf(a);
    if (id) this.props.history.push(`/loyalty/accounts/${id}`);
  };

  columns() {
    return [
      {
        key: "customer",
        label: "Member",
        render: (a) => (
          <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
            <Avatar name={this.nameOf(a)} />
            <div>
              <div style={{ fontSize: 15, color: C.ink }}>{this.nameOf(a)}</div>
              <div style={{ fontSize: 12, color: C.taupeLight }}>{this.contactOf(a)}</div>
            </div>
          </div>
        ),
      },
      {
        key: "tier",
        label: "Tier",
        width: 120,
        render: (a) => (
          <Chip tone={tierTone(a.tier)}>
            <span style={{ textTransform: "capitalize" }}>{a.tier || "standard"}</span>
          </Chip>
        ),
      },
      {
        key: "balance",
        label: "Balance",
        align: "right",
        width: 120,
        render: (a) => (
          <span style={{ fontSize: 15, fontWeight: 500, color: C.ink }}>
            {num(a.pointsBalance)}
          </span>
        ),
      },
      {
        key: "earned",
        label: "Earned",
        align: "right",
        width: 110,
        render: (a) => <span style={{ color: C.taupe }}>{num(a.totalEarned)}</span>,
      },
      {
        key: "redeemed",
        label: "Redeemed",
        align: "right",
        width: 110,
        render: (a) => <span style={{ color: C.taupe }}>{num(a.totalRedeemed)}</span>,
      },
      {
        key: "action",
        label: "",
        width: 60,
        align: "right",
        render: (a) => (
          <div onClick={(e) => e.stopPropagation()}>
            <IconBtn
              icon="visibility"
              title={`Open ${this.nameOf(a)}`}
              onClick={() => this.openMember(a)}
            />
          </div>
        ),
      },
    ];
  }

  render() {
    const { isLoaded, total, items, search } = this.state;
    const rows = this.visibleRows();
    const outstanding = items.reduce((s, a) => s + Number(a.pointsBalance || 0), 0);
    const earned = items.reduce((s, a) => s + Number(a.totalEarned || 0), 0);
    const redeemed = items.reduce((s, a) => s + Number(a.totalRedeemed || 0), 0);
    // The call fetches one page of 100; say so rather than implying the tiles
    // cover a programme that may be larger.
    const scope = total > items.length ? `across the ${items.length} shown` : "across all members";

    return (
      <ConsolePage
        title="Loyalty members"
        subtitle={
          isLoaded
            ? `${total} member${total === 1 ? "" : "s"} · points are shared across every branch`
            : "Loading members…"
        }
        actions={
          <React.Fragment>
            <SearchPill
              value={search}
              onChange={(v) => this.setState({ search: v })}
              placeholder="Name, email or phone"
              width={250}
            />
            <Btn
              kind="quiet"
              icon="tune"
              onClick={() => this.props.history.push("/loyalty/settings")}
            >
              Settings
            </Btn>
          </React.Fragment>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <StatRow>
            <StatCard icon="group" value={num(total)} label="Members enrolled" />
            <StatCard icon="account_balance_wallet" value={num(outstanding)} label={`Points outstanding, ${scope}`} />
            <StatCard icon="trending_up" value={num(earned)} label={`Points earned, ${scope}`} />
            <StatCard icon="redeem" value={num(redeemed)} label={`Points redeemed, ${scope}`} />
          </StatRow>

          <Panel style={{ padding: 0 }}>
            <div
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 12, padding: "16px 22px", borderBottom: `1px solid ${C.panel}`,
              }}
            >
              <div style={{ fontSize: 17, fontWeight: 500, color: C.ink }}>
                Members{search.trim() ? ` matching “${search.trim()}”` : ""}
              </div>
              <div style={{ fontSize: 14, color: C.taupeLight }}>
                {rows.length} of {items.length} shown
              </div>
            </div>

            <div style={{ padding: "0 10px" }}>
              {!isLoaded ? (
                <div style={{ padding: "48px 0", textAlign: "center", color: C.taupeLight }}>
                  Loading members…
                </div>
              ) : (
                <Table
                  columns={this.columns()}
                  rows={rows}
                  rowKey="_id"
                  empty={
                    items.length === 0
                      ? "No one has joined the loyalty programme yet"
                      : "No member matches this search"
                  }
                  onRowClick={this.openMember}
                />
              )}
            </div>
          </Panel>
        </div>
      </ConsolePage>
    );
  }
}

export default LoyaltyAccounts;
